import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Plus, Printer, Trash, PencilSimple, Calendar, X, CalendarBlank, ListDashes } from '@phosphor-icons/react';
import ProgramaSemanal from '@/components/ProgramaSemanal';
import ImportarProgramaFoto from '@/components/ImportarProgramaFoto';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('es-GT') : '—';
const toISODate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

// Devuelve el lunes de la semana de una fecha (ISO date)
const mondayOf = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = d.getDay(); // 0=dom
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return toISODate(d);
};

export default function Programacion() {
  const { selectedBusiness, user } = useOutletContext();
  const [view, setView] = useState('table'); // 'table' | 'calendar' | 'month'
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterOperador, setFilterOperador] = useState('all');
  const [prodUsers, setProdUsers] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const params = { business_id: selectedBusiness.id };
      if (view === 'calendar' || view === 'programa') params.week_start = weekStart;
      if (filterOperador !== 'all') params.operador_id = filterOperador;
      // El backend acepta solo week_start; para 'month' cargamos todo y filtramos abajo.
      const res = await api.get('/programacion/list', { params });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando programaciones');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, view, weekStart, filterOperador]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedBusiness) return;
    api.get('/programacion/produccion-users', { params: { business_id: selectedBusiness.id } })
      .then(r => setProdUsers(r.data?.users || []))
      .catch(() => setProdUsers([]));
  }, [selectedBusiness]);

  const removeItem = async (id) => {
    if (!window.confirm('¿Eliminar esta programación?')) return;
    try {
      await api.delete(`/programacion/${id}`);
      toast.success('Programación eliminada');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const downloadPdf = async (id, numero) => {
    try {
      const res = await api.get(`/programacion/${id}/orden-entrega/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `OrdenEntrega_${numero || id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error('Error generando PDF'); }
  };

  const [showPreview, setShowPreview] = useState(false);

  const openPreview = () => setShowPreview(true);

  const downloadExport = async (format, mode = 'resumen') => {
    try {
      const params = { business_id: selectedBusiness?.id, mode };
      if (view === 'calendar') params.week_start = weekStart;
      else if (view === 'month') params.month_start = monthAnchor;
      const path = format === 'excel' ? '/programacion/export/excel' : '/programacion/export/pdf';
      const res = await api.get(path, { params, responseType: 'blob' });
      const mime = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = `Programaciones_${mode}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Exportado a ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error exportando');
    }
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(toISODate(d));
  };

  // Agrupar por día para vista calendario
  const days = useMemo(() => {
    const out = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const key = toISODate(day);
      const inThisDay = items.filter(it => {
        const fi = it.fecha_inicio;
        const ff = it.fecha_fin || it.fecha_inicio;
        return fi <= key && key <= ff;
      });
      out.push({ date: key, label: day.toLocaleDateString('es-GT', { weekday: 'short', day: '2-digit', month: 'short' }), items: inThisDay });
    }
    return out;
  }, [weekStart, items]);

  const openCreate = () => { setEditItem(null); setShowEdit(true); };
  const openEdit = (it) => { setEditItem(it); setShowEdit(true); };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="page-programacion">
      <div className="flex flex-wrap items-start gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shrink-0">
          <Calendar size={26} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text)]">Programación de Instalaciones</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">Planificación semanal · Vehículos · Instaladores · Orden de entrega</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-[var(--surface-muted)] rounded-full p-0.5">
            <button onClick={() => setView('table')} className={`px-3 py-1 rounded-full text-xs font-bold ${view === 'table' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="prog-view-table"><ListDashes size={12} className="inline mr-1" />Tabla</button>
            <button onClick={() => setView('calendar')} className={`px-3 py-1 rounded-full text-xs font-bold ${view === 'calendar' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="prog-view-calendar"><CalendarBlank size={12} className="inline mr-1" />Semana</button>
            <button onClick={() => setView('programa')} className={`px-3 py-1 rounded-full text-xs font-bold ${view === 'programa' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="prog-view-programa"><Printer size={12} className="inline mr-1" />Programa</button>
            <button onClick={() => setView('month')} className={`px-3 py-1 rounded-full text-xs font-bold ${view === 'month' ? 'bg-[var(--surface)] shadow-sm text-[var(--text)]' : 'text-[var(--text-muted)]'}`} data-testid="prog-view-month"><CalendarBlank size={12} className="inline mr-1" />Mes</button>
          </div>
          <select
            value={filterOperador}
            onChange={(e) => setFilterOperador(e.target.value)}
            className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md px-2 py-1 text-xs font-bold text-[var(--text)]"
            data-testid="prog-filter-operador"
            title="Filtrar por operador"
          >
            <option value="all">👤 Todos los operadores</option>
            {prodUsers.map(u => <option key={u.id} value={u.id}>👤 {u.name}</option>)}
          </select>
          <button onClick={openPreview} className="px-3 py-1.5 bg-blue-600/10 border border-blue-600/30 text-blue-700 dark:text-blue-300 hover:bg-blue-600/20 rounded-md text-xs font-bold flex items-center gap-1" data-testid="prog-preview-btn" title="Vista previa antes de exportar">
            👁️ Vista previa
          </button>
          {(user?.role || '').toLowerCase() === 'admin' && (
            <button
              onClick={async () => {
                if (!window.confirm('¿Reenviar ahora el reporte semanal de programación por email (PDF + Excel) a los destinatarios?')) return;
                try {
                  const res = await api.post('/programacion/admin/send-weekly-report');
                  if (res.data?.status === 'success') toast.success(`Reporte enviado a ${(res.data.to || []).length} destinatarios`);
                  else toast.error(res.data?.error || res.data?.reason || 'No se pudo enviar');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Error enviando reporte');
                }
              }}
              className="px-3 py-1.5 bg-emerald-600/10 border border-emerald-600/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-600/20 rounded-md text-xs font-bold flex items-center gap-1"
              data-testid="prog-resend-weekly-report-btn"
              title="Reenviar el email semanal (martes 8 AM) manualmente"
            >
              ✉️ Reenviar reporte semanal
            </button>
          )}
          <button onClick={() => setShowImport(true)} className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 rounded-md text-xs font-bold flex items-center gap-1" data-testid="prog-importar-foto" title="Subir foto de tu cuadro de Excel — la IA crea las programaciones">
            📷 Importar desde foto
          </button>
          <button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1" data-testid="prog-nueva">
            <Plus size={14} weight="bold" /> Nueva programación
          </button>
        </div>
      </div>

      {(view === 'calendar' || view === 'programa') && (
        <div className="mb-3 bg-[var(--surface-muted)] rounded-md px-3 py-2 space-y-2">
          <div className="flex items-center justify-between">
            <button onClick={() => shiftWeek(-1)} className="px-2 py-1 text-xs font-bold hover:bg-[var(--surface)] rounded">‹ Semana anterior</button>
            <p className="text-sm font-black text-[var(--text)]">Semana del {new Date(weekStart).toLocaleDateString('es-GT', { day: '2-digit', month: 'long' })}</p>
            <button onClick={() => shiftWeek(1)} className="px-2 py-1 text-xs font-bold hover:bg-[var(--surface)] rounded">Semana siguiente ›</button>
          </div>
          <div className="flex items-center gap-2 flex-wrap border-t border-[var(--border)] pt-2">
            <span className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Ir a:</span>
            <input
              type="month"
              value={weekStart.slice(0, 7)}
              onChange={(e) => {
                if (!e.target.value) return;
                // Ir al primer lunes del mes elegido
                const [yy, mm] = e.target.value.split('-').map(Number);
                const firstDay = new Date(yy, mm - 1, 1);
                setWeekStart(mondayOf(firstDay));
              }}
              className="bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-0.5 text-xs text-[var(--text)]"
              data-testid="week-month-picker"
            />
            <select
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="flex-1 bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-0.5 text-xs text-[var(--text)] min-w-[220px]"
              data-testid="week-of-month-selector"
            >
              {(() => {
                // Generar semanas del mes que contiene weekStart
                const anchor = new Date(weekStart);
                const yy = anchor.getFullYear();
                const mm = anchor.getMonth();
                const firstDay = new Date(yy, mm, 1);
                const firstMonday = mondayOf(firstDay);
                const options = [];
                let cur = new Date(firstMonday);
                let idx = 1;
                while (idx <= 6) {
                  const iso = cur.toISOString().slice(0, 10);
                  const end = new Date(cur); end.setDate(cur.getDate() + 6);
                  // Solo incluir semanas que toquen el mes anchor
                  if (cur.getMonth() === mm || end.getMonth() === mm) {
                    options.push({ iso, label: `Semana ${idx} · ${cur.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' })} → ${end.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' })}` });
                    idx++;
                  }
                  cur.setDate(cur.getDate() + 7);
                  if (cur.getMonth() > mm && cur.getFullYear() >= yy) break;
                }
                return options.map(o => <option key={o.iso} value={o.iso}>{o.label}</option>);
              })()}
            </select>
          </div>
        </div>
      )}

      {view === 'month' && (
        <div className="flex items-center justify-between mb-3 bg-[var(--surface-muted)] rounded-md px-3 py-2">
          <button onClick={() => { const d = new Date(monthAnchor); d.setMonth(d.getMonth() - 1); setMonthAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`); }} className="px-2 py-1 text-xs font-bold hover:bg-[var(--surface)] rounded">‹ Mes anterior</button>
          <p className="text-sm font-black text-[var(--text)]">{new Date(monthAnchor).toLocaleDateString('es-GT', { month: 'long', year: 'numeric' })}</p>
          <button onClick={() => { const d = new Date(monthAnchor); d.setMonth(d.getMonth() + 1); setMonthAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`); }} className="px-2 py-1 text-xs font-bold hover:bg-[var(--surface)] rounded">Mes siguiente ›</button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[var(--text-muted)]">Cargando…</p>
      ) : view === 'month' ? (
        <MonthView monthAnchor={monthAnchor} items={items} onEdit={openEdit} />
      ) : view === 'programa' ? (
        <ProgramaSemanal items={items} weekStart={weekStart} business={selectedBusiness} onEdit={openEdit} />
      ) : view === 'calendar' ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {days.map((d) => (
            <div key={d.date} className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2 min-h-[140px]" data-testid={`prog-day-${d.date}`}>
              <p className="text-[10px] uppercase font-black text-[var(--text-muted)] tracking-wider mb-2">{d.label}</p>
              <div className="space-y-1.5">
                {d.items.length === 0 ? (
                  <p className="text-[10px] text-[var(--text-faint)] italic">—</p>
                ) : d.items.map((it) => (
                  <button key={it.id} onClick={() => openEdit(it)} className="w-full text-left bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded p-2" data-testid={`prog-card-${it.id}`}>
                    <p className="text-[10px] font-black text-purple-700 dark:text-purple-300">{it.ot_numero || '—'}</p>
                    <p className="text-xs font-bold text-[var(--text)] truncate">{it.cliente_nombre}</p>
                    <p className="text-[10px] text-[var(--text-muted)] truncate">👷 {it.instalador_responsable_nombre || '—'}</p>
                    {it.operador_nombre && <p className="text-[10px] text-[var(--text-muted)] truncate">👤 {it.operador_nombre}</p>}
                    {it.cobrar_final && <span className="text-[9px] px-1 bg-red-500/20 text-red-700 dark:text-red-300 rounded font-bold">COBRAR</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase font-bold text-[var(--text-muted)] border-b border-[var(--border)]">
                <th className="text-left py-2 px-2">OT</th>
                <th className="text-left py-2 px-2">Cliente / Dirección</th>
                <th className="text-left py-2 px-2">Instalador · equipo</th>
                <th className="text-left py-2 px-2">Operador</th>
                <th className="text-left py-2 px-2">Vehículo</th>
                <th className="text-left py-2 px-2">Inicio → Fin</th>
                <th className="text-right py-2 px-2">m² / mts</th>
                <th className="text-right py-2 px-2">Flete + Viáticos</th>
                <th className="text-left py-2 px-2">Obs.</th>
                <th className="text-right py-2 px-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={10} className="py-8 text-center text-[var(--text-muted)] italic">Sin programaciones. Crea la primera con &quot;Nueva programación&quot;.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className="border-b border-[var(--border)]/40 hover:bg-[var(--surface-muted)]" data-testid={`prog-row-${it.id}`}>
                  <td className="py-2 px-2 font-mono font-black text-[var(--text)]">{it.ot_numero || '—'}</td>
                  <td className="py-2 px-2">
                    <p className="font-bold text-[var(--text)] truncate max-w-[220px]">{it.cliente_nombre}</p>
                    <p className="text-[10px] text-[var(--text-muted)] truncate max-w-[220px]">📍 {it.direccion}</p>
                  </td>
                  <td className="py-2 px-2 text-[var(--text)]">
                    {it.instaladores && it.instaladores.length > 0 ? (
                      <>
                        <p className="truncate max-w-[220px]">
                          {it.instaladores.filter(x => x.es_responsable).map(x => x.nombre).join(', ') || it.instaladores[0]?.nombre}
                          {it.instaladores.length > 1 && <span className="text-[10px] text-[var(--text-muted)]"> +{it.instaladores.length - 1}</span>}
                        </p>
                        <p className="text-[10px] text-[var(--text-muted)]">
                          {it.instaladores.length} {it.instaladores.length === 1 ? 'persona' : 'personas'}
                        </p>
                      </>
                    ) : (
                      <>
                        <p>{it.instalador_responsable_nombre || '—'}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{it.equipo_personas || 1} pers.</p>
                      </>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--text)] text-xs">
                    {it.operador_nombre ? <span title="Operador administrativo">👤 {it.operador_nombre}</span> : <span className="text-[var(--text-faint)]">—</span>}
                  </td>
                  <td className="py-2 px-2 text-[var(--text-muted)] text-xs">{it.vehiculo || '—'}<br /><span className="font-mono">{it.placas || ''}</span></td>
                  <td className="py-2 px-2 text-xs">
                    <p className="text-[var(--text)]">{fmtDate(it.fecha_inicio)}</p>
                    <p className="text-[var(--text-muted)]">→ {fmtDate(it.fecha_fin)}</p>
                  </td>
                  <td className="py-2 px-2 text-right text-xs text-[var(--text)]">
                    {it.m2 ? `${it.m2} m²` : ''}
                    {it.metros_lineales ? `${it.metros_lineales} mL` : ''}
                  </td>
                  <td className="py-2 px-2 text-right text-xs text-[var(--text-muted)]">{fmtQ((it.flete_q || 0) + (it.viaticos_q || 0))}</td>
                  <td className="py-2 px-2 text-xs">
                    {it.estado_ejecucion === 'terminada' ? (
                      <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded font-bold block mb-0.5">✅ TERMINADA</span>
                    ) : (
                      <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-700 dark:text-amber-300 rounded font-bold block mb-0.5" title={it.motivo_pendiente || 'Aún pendiente'}>⏳ PENDIENTE{it.motivo_pendiente ? ' *' : ''}</span>
                    )}
                    {it.cobrar_final && <span className="text-[9px] px-1 bg-red-500/20 text-red-700 dark:text-red-300 rounded font-bold block mb-0.5">COBRAR FINAL</span>}
                    <span className="text-[var(--text-muted)] truncate max-w-[160px] block" title={it.observaciones}>{it.observaciones || '—'}</span>
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <button onClick={() => downloadPdf(it.id, it.ot_numero)} title="Orden de entrega PDF" className="p-1 text-[var(--text-muted)] hover:text-blue-600" data-testid={`prog-pdf-${it.id}`}><Printer size={14} /></button>
                    <button onClick={() => openEdit(it)} title="Editar" className="p-1 text-[var(--text-muted)] hover:text-amber-600" data-testid={`prog-edit-${it.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => removeItem(it.id)} title="Eliminar" className="p-1 text-[var(--text-muted)] hover:text-red-600" data-testid={`prog-del-${it.id}`}><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 0 && <p className="text-[10px] text-[var(--text-muted)] p-2">{items.length} programación(es)</p>}
        </div>
      )}

      {/* Carga de trabajo por instalador */}
      <CargaInstaladoresPanel
        business={selectedBusiness}
        view={view}
        weekStart={weekStart}
        monthAnchor={monthAnchor}
      />

      {showEdit && (
        <ProgramacionEditModal
          business={selectedBusiness}
          item={editItem}
          onClose={() => { setShowEdit(false); setEditItem(null); }}
          onSaved={() => { setShowEdit(false); setEditItem(null); load(); }}
        />
      )}
      {showImport && (
        <ImportarProgramaFoto
          business={selectedBusiness}
          weekStart={weekStart}
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}
      {showPreview && (
        <ReportPreviewModal
          business={selectedBusiness}
          view={view}
          weekStart={weekStart}
          monthAnchor={monthAnchor}
          onClose={() => setShowPreview(false)}
          onDownload={(format, mode) => downloadExport(format, mode)}
        />
      )}
    </div>
  );
}

const MonthView = ({ monthAnchor, items, onEdit }) => {
  const anchor = new Date(monthAnchor);
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const firstDay = new Date(y, m, 1);
  const lastDay = new Date(y, m + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7; // lunes=0
  const daysInMonth = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(d => <div key={d} className="text-[10px] uppercase font-black text-[var(--text-muted)] text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[100px]" />;
          const key = toISODate(d);
          const inThisDay = items.filter(it => (it.fecha_inicio || '') <= key && key <= (it.fecha_fin || it.fecha_inicio || ''));
          const isToday = key === toISODate(new Date());
          return (
            <div key={i} className={`min-h-[100px] rounded p-1.5 border ${isToday ? 'border-purple-500 bg-purple-500/5' : 'border-[var(--border)] bg-[var(--surface-muted)]/40'}`} data-testid={`month-day-${key}`}>
              <p className={`text-[10px] font-bold ${isToday ? 'text-purple-700 dark:text-purple-300' : 'text-[var(--text-muted)]'}`}>{d.getDate()}</p>
              <div className="space-y-1 mt-1">
                {inThisDay.slice(0, 3).map((it) => (
                  <button key={it.id} onClick={() => onEdit(it)} className="w-full text-left bg-purple-500/15 hover:bg-purple-500/25 rounded px-1 py-0.5 truncate" title={`${it.ot_numero} · ${it.cliente_nombre}`} data-testid={`month-card-${it.id}`}>
                    <span className="text-[9px] font-black text-purple-700 dark:text-purple-300">{it.ot_numero || '·'}</span>
                    <span className="text-[9px] text-[var(--text)] ml-1">{it.cliente_nombre}</span>
                  </button>
                ))}
                {inThisDay.length > 3 && <p className="text-[9px] text-[var(--text-muted)]">+{inThisDay.length - 3}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CargaInstaladoresPanel = ({ business, view, weekStart, monthAnchor }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!business) return;
    setLoading(true);
    const params = { business_id: business.id };
    if (view === 'calendar') params.week_start = weekStart;
    else if (view === 'month') params.month_start = monthAnchor;
    api.get('/programacion/carga-instaladores', { params })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [business, view, weekStart, monthAnchor]);

  if (!data && !loading) return null;
  const items = data?.instaladores || [];
  const withLoad = items.filter(i => i.ots_count > 0);
  const idle = items.filter(i => i.ots_count === 0 && i.activo);

  const barColor = (pct) => pct >= 80 ? 'bg-red-500' : pct >= 50 ? 'bg-amber-500' : pct >= 20 ? 'bg-emerald-500' : 'bg-blue-500';

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md mt-4" data-testid="carga-instaladores-panel">
      <button onClick={() => setCollapsed(v => !v)} className="w-full flex items-center justify-between px-4 py-2 hover:bg-[var(--surface-muted)] rounded-t-md">
        <div className="flex items-center gap-2">
          <span className="text-lg">⚒️</span>
          <div className="text-left">
            <p className="text-sm font-black text-[var(--text)]">Carga de trabajo por instalador</p>
            <p className="text-[10px] text-[var(--text-muted)]">
              {data ? `${withLoad.length} con OTs · ${idle.length} disponibles · ${data.total_asignaciones} asignaciones sobre ${data.total_ots} OTs` : '—'}
            </p>
          </div>
        </div>
        <span className="text-xs text-[var(--text-muted)]">{collapsed ? '▶ ver' : '▼ ocultar'}</span>
      </button>
      {!collapsed && (
        <div className="p-4 border-t border-[var(--border)]">
          {loading ? (
            <p className="text-xs text-[var(--text-muted)]">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic">Sin datos en este período.</p>
          ) : (
            <>
              {withLoad.length > 0 && (
                <div className="space-y-2">
                  {withLoad.map(inst => (
                    <div key={inst.instalador_id || inst.nombre} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center py-1.5 border-b border-[var(--border)]/40" data-testid={`carga-row-${inst.nombre}`}>
                      <div className="md:col-span-3">
                        <p className="text-sm font-bold text-[var(--text)] truncate">
                          {inst.nombre}
                          {inst.rol === 'ayudante' && <span className="ml-1 text-[9px] px-1 py-0.5 bg-purple-500/15 text-purple-600 dark:text-purple-300 rounded font-bold">AYUD.</span>}
                          {!inst.activo && <span className="ml-1 text-[9px] px-1 py-0.5 bg-red-500/15 text-red-600 rounded font-bold">INACT.</span>}
                        </p>
                        <p className="text-[10px] text-[var(--text-muted)]">
                          {inst.modalidad || '—'}{(inst.especialidades || []).length > 0 && ` · ${inst.especialidades.slice(0, 2).join(', ')}`}
                        </p>
                      </div>
                      <div className="md:col-span-2 flex gap-2 text-xs">
                        <span className="bg-[var(--surface-muted)] px-1.5 py-0.5 rounded"><b className="text-[var(--text)]">{inst.ots_count}</b> OTs</span>
                        {inst.ots_como_responsable > 0 && <span className="text-purple-600 dark:text-purple-300" title="Como responsable">★{inst.ots_como_responsable}</span>}
                      </div>
                      <div className="md:col-span-2 text-xs text-[var(--text-muted)]">
                        {inst.dias_trabajo} día{inst.dias_trabajo === 1 ? '' : 's'}
                      </div>
                      <div className="md:col-span-4">
                        <div className="w-full bg-[var(--surface-muted)] rounded-full h-2 overflow-hidden">
                          <div className={`h-full ${barColor(inst.utilizacion_pct)}`} style={{ width: `${Math.min(100, inst.utilizacion_pct)}%` }} />
                        </div>
                      </div>
                      <div className="md:col-span-1 text-right">
                        <span className="text-xs font-black text-[var(--text)]">{inst.utilizacion_pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {idle.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[var(--border)]/40">
                  <p className="text-[10px] uppercase font-black text-emerald-600 dark:text-emerald-400 mb-2">💤 Disponibles ({idle.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {idle.map(inst => (
                      <span key={inst.instalador_id} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-bold" data-testid={`carga-idle-${inst.nombre}`}>
                        {inst.nombre}
                        {inst.rol === 'ayudante' && ' 🤝'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ProgramacionEditModal = ({ business, item, onClose, onSaved }) => {
  const isEdit = !!item;
  // Clave única para el borrador en localStorage
  const draftKey = `prog_draft_${business?.id || 'none'}_${isEdit ? item.id : 'new'}`;
  const [ots, setOts] = useState([]);
  const [users, setUsers] = useState([]);
  const [prodUsers, setProdUsers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [operadorMode, setOperadorMode] = useState(() => item?.operador_id ? 'select' : (item?.operador_nombre ? 'other' : 'select'));
  const [draftRestored, setDraftRestored] = useState(false);
  // Estado inicial "limpio" (usado como referencia y también para descartar borrador)
  const initialF = {
    ot_id: item?.ot_id || '',
    ot_numero: item?.ot_numero || '',
    cliente_nombre: item?.cliente_nombre || '',
    direccion: item?.direccion || '',
    vendor_id: item?.vendor_id || '',
    vendor_name: item?.vendor_name || '',
    instaladores: item?.instaladores || (item?.instalador_responsable_nombre ? [{
      id: item.instalador_responsable_id || null,
      nombre: item.instalador_responsable_nombre,
      telefono: item.instalador_telefono || '',
      rol: 'instalador',
      es_responsable: true,
    }] : []),
    operador_id: item?.operador_id || '',
    operador_nombre: item?.operador_nombre || '',
    equipo_personas: item?.equipo_personas || 1,
    descripcion_trabajo: item?.descripcion_trabajo || '',
    vehiculo: item?.vehiculo || '',
    placas: item?.placas || '',
    flete_q: item?.flete_q || 0,
    viaticos_q: item?.viaticos_q || 0,
    m2: item?.m2 || '',
    metros_lineales: item?.metros_lineales || '',
    fecha_inicio: item?.fecha_inicio || toISODate(new Date()),
    fecha_fin: item?.fecha_fin || toISODate(new Date()),
    dias_estimados: item?.dias_estimados || 1,
    horas_extras: item?.horas_extras || 0,
    observaciones: item?.observaciones || '',
    cobrar_final: item?.cobrar_final || false,
    estado_ejecucion: item?.estado_ejecucion || 'pendiente',
    motivo_pendiente: item?.motivo_pendiente || '',
  };
  const [f, setF] = useState(initialF);
  // Metadata del borrador (si existe): { savedAt: iso, business_id, ... }
  const [draftMeta, setDraftMeta] = useState(null);

  // Al montar: buscar borrador guardado
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.savedAt && parsed.data) {
        // Verificar que el borrador tenga algo relevante (no vacío)
        const hasContent = parsed.data.cliente_nombre?.trim()
          || parsed.data.descripcion_trabajo?.trim()
          || parsed.data.direccion?.trim()
          || (parsed.data.instaladores || []).length > 0;
        if (hasContent) setDraftMeta(parsed);
      }
    } catch { /* ignore corrupt */ }
  }, []);

  // Auto-guardado del borrador con debounce (400ms) — solo si no es edición o si hay cambios reales
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        // No guardar draft si nada relevante fue escrito (evita borrador vacío)
        const hasContent = f.cliente_nombre?.trim()
          || f.descripcion_trabajo?.trim()
          || f.direccion?.trim()
          || (f.instaladores || []).length > 0
          || f.ot_id;
        if (!hasContent) return;
        localStorage.setItem(draftKey, JSON.stringify({
          savedAt: new Date().toISOString(),
          business_id: business?.id,
          isEdit,
          item_id: item?.id || null,
          data: f,
        }));
      } catch { /* localStorage full or unavailable */ }
    }, 400);
    return () => clearTimeout(t);
  }, [f]);

  const restoreDraft = () => {
    if (!draftMeta) return;
    setF(draftMeta.data);
    setDraftRestored(true);
    setDraftMeta(null);
    toast.success('✅ Borrador restaurado. Revisa y guarda para confirmar.');
  };
  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setDraftMeta(null);
    toast.info('Borrador descartado.');
  };

  useEffect(() => {
    (async () => {
      try {
        const [otRes, uRes, pRes, iRes] = await Promise.all([
          api.get('/produccion/ots', { params: { business_id: business.id, limit: 300 } }),
          api.get('/users').catch(() => ({ data: [] })),
          api.get('/programacion/produccion-users', { params: { business_id: business.id } }).catch(() => ({ data: { users: [] } })),
          api.get('/instaladores', { params: { activo: true } }).catch(() => ({ data: { items: [] } })),
        ]);
        setOts(otRes.data?.ots || []);
        setUsers(uRes.data || []);
        setProdUsers(pRes.data?.users || []);
        setInstallers(iRes.data?.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, [business]);

  // Al elegir una OT, auto-poblar cliente, dirección, vendedor, descripción, m² etc.
  const handleOtSelect = async (otId) => {
    if (!otId) {
      setF(prev => ({ ...prev, ot_id: '', ot_numero: '' }));
      return;
    }
    try {
      const { data: ot } = await api.get(`/produccion/ot/${otId}`);
      setF(prev => ({
        ...prev,
        ot_id: otId,
        ot_numero: ot.numero || prev.ot_numero,
        cliente_nombre: ot.cliente?.nombre || prev.cliente_nombre,
        direccion: ot.direccion_proyecto || ot.cliente?.direccion || prev.direccion,
        vendor_id: ot.vendor_id || prev.vendor_id,
        vendor_name: ot.vendor_name || prev.vendor_name,
        descripcion_trabajo: ot.descripcion || prev.descripcion_trabajo,
        m2: (ot.m2 != null ? ot.m2 : prev.m2),
        metros_lineales: (ot.metros_lineales != null ? ot.metros_lineales : prev.metros_lineales),
      }));
      toast.success('Datos jalados de la OT');
    } catch (e) {
      // fallback: solo setear ot_id
      setF(prev => ({ ...prev, ot_id: otId }));
    }
  };

  const addInstalador = (inst) => {
    if (!inst) return;
    if (f.instaladores.some(x => x.id === inst.id)) return;
    setF(prev => ({
      ...prev,
      instaladores: [
        ...prev.instaladores,
        { id: inst.id, nombre: inst.nombre, telefono: inst.telefono || '', rol: inst.rol || 'instalador', es_responsable: prev.instaladores.length === 0 },
      ],
    }));
  };
  const removeInstalador = (idx) => {
    setF(prev => {
      const arr = prev.instaladores.filter((_, i) => i !== idx);
      // Si sacamos al responsable y queda alguien, marcarlo
      if (!arr.some(x => x.es_responsable) && arr.length > 0) arr[0].es_responsable = true;
      return { ...prev, instaladores: arr };
    });
  };
  const setResponsable = (idx) => {
    setF(prev => ({
      ...prev,
      instaladores: prev.instaladores.map((x, i) => ({ ...x, es_responsable: i === idx })),
    }));
  };

  const submit = async () => {
    if (!f.cliente_nombre.trim()) return toast.error('Nombre del cliente requerido');
    if (!f.fecha_inicio || !f.fecha_fin) return toast.error('Fechas de inicio y fin requeridas');
    if (f.estado_ejecucion === 'pendiente' && !(f.motivo_pendiente || '').trim()) {
      return toast.error('Debes indicar el motivo por el que quedó pendiente');
    }
    try {
      const payload = { ...f, business_id: business.id, m2: f.m2 ? Number(f.m2) : null, metros_lineales: f.metros_lineales ? Number(f.metros_lineales) : null, equipo_personas: Number(f.equipo_personas || 1), dias_estimados: Number(f.dias_estimados || 1), horas_extras: Number(f.horas_extras || 0), flete_q: Number(f.flete_q || 0), viaticos_q: Number(f.viaticos_q || 0) };
      if (isEdit) {
        await api.patch(`/programacion/${item.id}`, payload);
        toast.success('Programación actualizada');
      } else {
        await api.post('/programacion', payload);
        toast.success('Programación creada');
      }
      // Limpiar borrador tras guardar exitoso
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const inp = 'w-full px-2 py-1.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded text-sm';
  const lbl = 'text-[10px] uppercase font-bold text-[var(--text-muted)]';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-3xl w-full max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="modal-programacion-edit">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-black text-[var(--text)]">{isEdit ? 'Editar programación' : 'Nueva programación'}</h2>
          <div className="flex items-center gap-2">
            {draftRestored && <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400" data-testid="draft-restored-badge">✅ Borrador restaurado</span>}
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {draftMeta && !draftRestored && (
          <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/40 rounded flex items-start gap-2" data-testid="draft-banner">
            <span className="text-lg">💾</span>
            <div className="flex-1">
              <p className="text-xs font-black text-amber-700 dark:text-amber-300">
                Tenés un borrador sin guardar de {new Date(draftMeta.savedAt).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-[10px] text-amber-700 dark:text-amber-400">
                Cliente: <b>{draftMeta.data?.cliente_nombre || '(sin nombre)'}</b>
                {draftMeta.data?.direccion && ` · Dirección: ${draftMeta.data.direccion}`}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={restoreDraft} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold" data-testid="draft-restore-btn">Restaurar</button>
              <button onClick={discardDraft} className="px-2 py-1 text-[10px] font-bold text-[var(--text-muted)] hover:text-red-500" data-testid="draft-discard-btn">Descartar</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className={lbl}>OT vinculada (opcional)</label>
            <select value={f.ot_id} onChange={(e) => handleOtSelect(e.target.value)} className={inp} data-testid="prog-ot-select">
              <option value="">— sin OT vinculada —</option>
              {ots.map(o => <option key={o.id} value={o.id}>{o.numero} · {o.cliente?.nombre}</option>)}
            </select>
            {f.ot_id && (
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">✓ Cliente, dirección, vendedor, descripción y m² se auto-completan desde la OT.</p>
            )}
          </div>
          <div>
            <label className={lbl}>N° OT (manual)</label>
            <input value={f.ot_numero} onChange={(e) => setF({ ...f, ot_numero: e.target.value })} className={inp} placeholder="OT-2036" />
          </div>
          <div>
            <label className={lbl}>Cliente</label>
            <input value={f.cliente_nombre} onChange={(e) => setF({ ...f, cliente_nombre: e.target.value })} className={inp} data-testid="prog-cliente" />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Dirección de instalación</label>
            <input value={f.direccion} onChange={(e) => setF({ ...f, direccion: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Vendedor</label>
            <input value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })} className={inp} />
          </div>

          {/* ── Multi-select de Instaladores ────────────────────────────── */}
          <div className="col-span-2 bg-[var(--surface-muted)]/30 border border-[var(--border)] rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Instaladores asignados ({f.instaladores.length})</label>
              <a href="/instaladores" target="_blank" rel="noreferrer" className="text-[10px] text-purple-500 hover:underline">➕ Gestionar catálogo</a>
            </div>
            <select
              onChange={(e) => { const id = e.target.value; e.target.value = ''; if (!id) return; addInstalador(installers.find(i => i.id === id)); }}
              className={inp}
              data-testid="prog-instalador-add"
              defaultValue=""
            >
              <option value="">— Agregar instalador del catálogo… —</option>
              {installers.filter(i => !f.instaladores.some(x => x.id === i.id)).map(i => (
                <option key={i.id} value={i.id}>
                  {i.nombre}{i.rol ? ` · ${i.rol === 'instalador' ? '👷' : '🤝'} ${i.rol}` : ''}{i.especialidades?.length ? ` · ${i.especialidades.join(', ')}` : ''}
                </option>
              ))}
            </select>
            {installers.length === 0 && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                Aún no hay instaladores. <a href="/instaladores" target="_blank" rel="noreferrer" className="underline font-bold">Crea el catálogo</a> primero.
              </p>
            )}
            <div className="mt-2 space-y-1">
              {f.instaladores.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic py-2">Sin instaladores asignados. Agrega al menos uno.</p>
              ) : f.instaladores.map((ins, idx) => (
                <div key={ins.id || idx} className={`flex items-center gap-2 bg-[var(--bg)] border rounded px-2 py-1.5 ${ins.es_responsable ? 'border-purple-500' : 'border-[var(--border)]'}`} data-testid={`prog-inst-row-${idx}`}>
                  <button type="button" onClick={() => setResponsable(idx)} title={ins.es_responsable ? 'Responsable de esta OT' : 'Marcar como responsable'}
                          className={`text-xs w-6 h-6 rounded-full flex items-center justify-center font-black ${ins.es_responsable ? 'bg-purple-600 text-white' : 'bg-[var(--surface-muted)] text-[var(--text-muted)] hover:bg-purple-500/20'}`}>
                    ★
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[var(--text)] truncate">
                      {ins.nombre}
                      {ins.es_responsable && <span className="ml-1 text-[9px] px-1 bg-purple-500/20 text-purple-700 dark:text-purple-300 rounded font-black">RESPONSABLE</span>}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      {ins.rol === 'instalador' ? '👷 Instalador' : ins.rol === 'ayudante' ? '🤝 Ayudante' : '—'} · {ins.telefono || 'sin tel'}
                    </p>
                  </div>
                  <button type="button" onClick={() => removeInstalador(idx)} className="text-red-500 hover:bg-red-500/10 rounded p-1" data-testid={`prog-inst-remove-${idx}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className={lbl}>Cuántas personas van</label>
            <input type="number" min={1} value={f.equipo_personas} onChange={(e) => setF({ ...f, equipo_personas: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Operador (usuario administrativo de Producción)</label>
            <div className="flex gap-2">
              {operadorMode === 'select' ? (
                <select
                  value={f.operador_id}
                  onChange={(e) => {
                    const uid = e.target.value;
                    const u = prodUsers.find(x => x.id === uid);
                    setF(prev => ({ ...prev, operador_id: uid, operador_nombre: u?.name || '' }));
                  }}
                  className={`${inp} flex-1`}
                  data-testid="prog-operador-select"
                >
                  <option value="">— Sin operador —</option>
                  {prodUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role || 'user'})</option>
                  ))}
                </select>
              ) : (
                <input
                  value={f.operador_nombre}
                  onChange={(e) => setF({ ...f, operador_id: '', operador_nombre: e.target.value })}
                  placeholder="Nombre del operador (texto libre)"
                  className={`${inp} flex-1`}
                  data-testid="prog-operador-text"
                />
              )}
              <button
                type="button"
                onClick={() => {
                  const next = operadorMode === 'select' ? 'other' : 'select';
                  setOperadorMode(next);
                  if (next === 'other') setF(prev => ({ ...prev, operador_id: '' }));
                  else setF(prev => ({ ...prev, operador_nombre: '' }));
                }}
                className="px-3 py-1.5 rounded-md border border-[var(--border)] text-[10px] font-bold text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)] whitespace-nowrap"
                data-testid="prog-operador-toggle"
                title="Alternar entre usuario del sistema y nombre libre"
              >
                {operadorMode === 'select' ? '✏️ Escribir otro' : '📋 Elegir de lista'}
              </button>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">Usuario a cargo administrativamente del proyecto — típicamente del área de Producción.</p>
          </div>
          <div>
            <label className={lbl}>Días estimados</label>
            <input type="number" min={1} value={f.dias_estimados} onChange={(e) => setF({ ...f, dias_estimados: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Descripción del trabajo</label>
            <textarea rows={2} value={f.descripcion_trabajo} onChange={(e) => setF({ ...f, descripcion_trabajo: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Vehículo</label>
            <input value={f.vehiculo} onChange={(e) => setF({ ...f, vehiculo: e.target.value })} className={inp} placeholder="Ej: Toyota Hilux blanca" />
          </div>
          <div>
            <label className={lbl}>Placas</label>
            <input value={f.placas} onChange={(e) => setF({ ...f, placas: e.target.value })} className={inp} placeholder="P-123ABC" />
          </div>
          <div>
            <label className={lbl}>Flete (Q)</label>
            <input type="number" step="0.01" value={f.flete_q} onChange={(e) => setF({ ...f, flete_q: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Viáticos (Q)</label>
            <input type="number" step="0.01" value={f.viaticos_q} onChange={(e) => setF({ ...f, viaticos_q: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>m² a instalar</label>
            <input type="number" step="0.01" value={f.m2} onChange={(e) => setF({ ...f, m2: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Metros lineales</label>
            <input type="number" step="0.01" value={f.metros_lineales} onChange={(e) => setF({ ...f, metros_lineales: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Fecha inicio</label>
            <input type="date" value={f.fecha_inicio} onChange={(e) => setF({ ...f, fecha_inicio: e.target.value })} className={inp} data-testid="prog-fecha-inicio" />
          </div>
          <div>
            <label className={lbl}>Fecha finalización</label>
            <input type="date" value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} className={inp} data-testid="prog-fecha-fin" />
          </div>
          <div>
            <label className={lbl}>Horas extras</label>
            <input type="number" step="0.5" value={f.horas_extras} onChange={(e) => setF({ ...f, horas_extras: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <label className={lbl}>Observaciones</label>
            <textarea rows={2} value={f.observaciones} onChange={(e) => setF({ ...f, observaciones: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 text-sm p-2 bg-red-500/10 border border-red-500/30 rounded cursor-pointer">
              <input type="checkbox" checked={f.cobrar_final} onChange={(e) => setF({ ...f, cobrar_final: e.target.checked })} data-testid="prog-cobrar-final" />
              <span className="text-[var(--text)]"><b>Cobrar el pago final</b> al terminar la instalación (aparecerá destacado en la orden de entrega)</span>
            </label>
          </div>

          {/* ── Estado de ejecución ─────────────────────────────────── */}
          <div className="col-span-2">
            <label className={lbl}>Estado de ejecución</label>
            <div className="flex gap-2">
              <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer ${f.estado_ejecucion === 'terminada' ? 'bg-emerald-500/15 border-emerald-500 text-emerald-700 dark:text-emerald-300' : 'bg-[var(--bg)] border-[var(--border)] text-[var(--text-muted)]'}`}>
                <input type="radio" name="estado_ejec" checked={f.estado_ejecucion === 'terminada'} onChange={() => setF({ ...f, estado_ejecucion: 'terminada', motivo_pendiente: '' })} data-testid="prog-estado-terminada" />
                <span className="text-sm font-bold">✅ Terminada</span>
              </label>
              <label className={`flex-1 flex items-center gap-2 p-2 rounded border cursor-pointer ${f.estado_ejecucion === 'pendiente' ? 'bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-300' : 'bg-[var(--bg)] border-[var(--border)] text-[var(--text-muted)]'}`}>
                <input type="radio" name="estado_ejec" checked={f.estado_ejecucion === 'pendiente'} onChange={() => setF({ ...f, estado_ejecucion: 'pendiente' })} data-testid="prog-estado-pendiente" />
                <span className="text-sm font-bold">⏳ Pendiente</span>
              </label>
            </div>
            {f.estado_ejecucion === 'pendiente' && (
              <textarea
                rows={2}
                value={f.motivo_pendiente}
                onChange={(e) => setF({ ...f, motivo_pendiente: e.target.value })}
                placeholder="Motivo por el que quedó pendiente (obligatorio) — ej: material incompleto, cliente no estuvo, clima…"
                className={`${inp} mt-2 border-amber-500/50`}
                data-testid="prog-motivo-pendiente"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold rounded border border-[var(--border)] text-[var(--text-muted)]">Cancelar</button>
          <button onClick={submit} className="px-3 py-1.5 text-xs font-bold rounded bg-purple-600 hover:bg-purple-700 text-white" data-testid="prog-submit">
            {isEdit ? 'Guardar cambios' : 'Crear programación'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─────────────────────── REPORT PREVIEW MODAL ───────────────────────
const ReportPreviewModal = ({ business, view, weekStart, monthAnchor, onClose, onDownload }) => {
  const [mode, setMode] = useState('resumen'); // 'resumen' | 'detallado'
  const [rango, setRango] = useState(view === 'month' ? 'month' : (view === 'calendar' ? 'week' : 'all'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWa, setShowWa] = useState(false);
  const [waPhone, setWaPhone] = useState('');
  const [waSending, setWaSending] = useState(false);

  // Contactos rápidos — editables en Configuración → Contactos WhatsApp
  const [quickContacts, setQuickContacts] = useState([]);
  useEffect(() => {
    api.get('/programacion/wa-quick-contacts').then(r => setQuickContacts(r.data.contacts || [])).catch(() => {});
  }, []);

  const sendWhatsapp = async () => {
    const raw = (waPhone || '').replace(/\D/g, '');
    if (raw.length < 8) { toast.error('Teléfono inválido'); return; }
    setWaSending(true);
    try {
      const payload = { business_id: business?.id, phone: raw, mode };
      if (rango === 'week') payload.week_start = weekStart;
      else if (rango === 'month') payload.month_start = monthAnchor;
      const res = await api.post('/programacion/export/whatsapp', payload);
      toast.success(`📱 PDF enviado a +${res.data.to}`);
      setShowWa(false);
      setWaPhone('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando por WhatsApp');
    } finally {
      setWaSending(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { business_id: business?.id, mode };
        if (rango === 'week') params.week_start = weekStart;
        else if (rango === 'month') params.month_start = monthAnchor;
        const res = await api.get('/programacion/preview', { params });
        if (!cancelled) setData(res.data);
      } catch (e) {
        toast.error('Error cargando preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, rango, weekStart, monthAnchor, business]);

  const rangoLabel = rango === 'week' ? `Semana del ${new Date(weekStart).toLocaleDateString('es-GT')}` :
                     rango === 'month' ? new Date(monthAnchor).toLocaleDateString('es-GT', { month: 'long', year: 'numeric' }) :
                     'Todas las programaciones';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] max-w-5xl w-full max-h-[92vh] flex flex-col relative overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="modal-report-preview">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black text-[var(--text)]">👁️ Vista previa del reporte</h2>
            <p className="text-[11px] text-[var(--text-muted)]">Ajusta modo y rango, luego descarga en PDF o Excel</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Controles */}
        <div className="px-5 py-3 border-b border-[var(--border)] flex flex-wrap items-center gap-3 bg-[var(--surface-muted)]">
          <div>
            <p className="text-[9px] uppercase font-black text-[var(--text-muted)] mb-1">Rango</p>
            <div className="flex bg-[var(--surface)] rounded-full p-0.5">
              <button onClick={() => setRango('week')} className={`px-3 py-1 text-xs font-bold rounded-full ${rango === 'week' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)]'}`} data-testid="prev-rango-week">Semana</button>
              <button onClick={() => setRango('month')} className={`px-3 py-1 text-xs font-bold rounded-full ${rango === 'month' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)]'}`} data-testid="prev-rango-month">Mes</button>
              <button onClick={() => setRango('all')} className={`px-3 py-1 text-xs font-bold rounded-full ${rango === 'all' ? 'bg-blue-600 text-white' : 'text-[var(--text-muted)]'}`} data-testid="prev-rango-all">Todo</button>
            </div>
          </div>
          <div>
            <p className="text-[9px] uppercase font-black text-[var(--text-muted)] mb-1">Modo</p>
            <div className="flex bg-[var(--surface)] rounded-full p-0.5">
              <button onClick={() => setMode('resumen')} className={`px-3 py-1 text-xs font-bold rounded-full ${mode === 'resumen' ? 'bg-purple-600 text-white' : 'text-[var(--text-muted)]'}`} data-testid="prev-mode-resumen">Resumen</button>
              <button onClick={() => setMode('detallado')} className={`px-3 py-1 text-xs font-bold rounded-full ${mode === 'detallado' ? 'bg-purple-600 text-white' : 'text-[var(--text-muted)]'}`} data-testid="prev-mode-detallado">Detallado</button>
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => onDownload('pdf', mode)} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold" data-testid="prev-download-pdf">📄 Descargar PDF</button>
            <button onClick={() => onDownload('excel', mode)} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold" data-testid="prev-download-excel">📊 Descargar Excel</button>
            <button onClick={() => setShowWa(true)} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold" data-testid="prev-send-whatsapp" title="Enviar el PDF por WhatsApp">📱 Enviar por WhatsApp</button>
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <p className="text-sm text-[var(--text-muted)]">Cargando vista previa…</p>
          ) : (
            <div data-testid="prev-content">
              {/* Header con logos */}
              <div className="flex items-center justify-between gap-4 border-b-2 border-blue-600 pb-3 mb-4">
                <img src="/marketing/sach-logo.png" alt="Grupo Sach" className="h-10 opacity-70" onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="text-center flex-1">
                  <h3 className="text-lg font-black text-blue-700 dark:text-blue-300">Programación de Instalaciones</h3>
                  <p className="text-[10px] text-[var(--text-muted)]">{rangoLabel} · Modo: <b>{mode.toUpperCase()}</b> · {data?.total || 0} programación(es)</p>
                </div>
                <img src="/marketing/soluciones-logo.png" alt="Soluciones Arquitectónicas" className="h-10 opacity-70" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
                  <p className="text-[9px] uppercase font-bold text-blue-700 dark:text-blue-300">Programaciones</p>
                  <p className="text-2xl font-black text-blue-900 dark:text-blue-200">{data?.total || 0}</p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
                  <p className="text-[9px] uppercase font-bold text-emerald-700 dark:text-emerald-300">Instaladores</p>
                  <p className="text-2xl font-black text-emerald-900 dark:text-emerald-200">{data?.installers_unique || 0}</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
                  <p className="text-[9px] uppercase font-bold text-red-700 dark:text-red-300">A cobrar final</p>
                  <p className="text-2xl font-black text-red-900 dark:text-red-200">{data?.cobrar_final || 0}</p>
                </div>
              </div>

              {/* Resumen por instalador */}
              {data?.by_installer?.length > 0 && (
                <>
                  <p className="text-[11px] uppercase font-black text-purple-700 dark:text-purple-300 mb-1">👷 Resumen por instalador</p>
                  <table className="w-full text-xs mb-4 border border-[var(--border)]">
                    <thead className="bg-purple-500/10">
                      <tr>
                        <th className="text-left px-2 py-1 text-[9px] uppercase font-bold text-purple-800 dark:text-purple-300">Instalador</th>
                        <th className="text-center px-2 py-1 text-[9px] uppercase font-bold text-purple-800 dark:text-purple-300">Proy.</th>
                        <th className="text-center px-2 py-1 text-[9px] uppercase font-bold text-purple-800 dark:text-purple-300">Días</th>
                        <th className="text-left px-2 py-1 text-[9px] uppercase font-bold text-purple-800 dark:text-purple-300">Vehículo · Placas</th>
                        <th className="text-left px-2 py-1 text-[9px] uppercase font-bold text-purple-800 dark:text-purple-300">Volumen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_installer.map((s, i) => (
                        <tr key={i} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1 font-bold text-[var(--text)]">{s.instalador}</td>
                          <td className="px-2 py-1 text-center font-black text-purple-700 dark:text-purple-300">{s.proyectos}</td>
                          <td className="px-2 py-1 text-center text-[var(--text-muted)]">{s.dias}d</td>
                          <td className="px-2 py-1 text-[var(--text-muted)]">{s.vehiculo || '—'} <span className="font-mono">{s.placas}</span></td>
                          <td className="px-2 py-1 text-[var(--text-muted)]">
                            {s.m2 > 0 ? `${s.m2} m²` : ''} {s.metros_lineales > 0 ? `${s.metros_lineales} mL` : ''} {(!s.m2 && !s.metros_lineales) ? '—' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* Detalle: tabla o cards por OT */}
              <p className="text-[11px] uppercase font-black text-blue-700 dark:text-blue-300 mb-1">
                {mode === 'detallado' ? '📋 Detalle completo por OT' : '📄 Tabla resumen'}
              </p>
              {mode === 'resumen' ? (
                <table className="w-full text-xs border border-[var(--border)]" data-testid="prev-resumen-table">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="px-2 py-1 text-left">OT</th>
                      <th className="px-2 py-1 text-left">Cliente</th>
                      <th className="px-2 py-1 text-left">Instalador</th>
                      <th className="px-2 py-1 text-left">Operador</th>
                      <th className="px-2 py-1 text-left">Inicio → Fin</th>
                      <th className="px-2 py-1 text-center">Cobrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.rows || []).map((r) => (
                      <tr key={r.id} className="border-t border-[var(--border)]">
                        <td className="px-2 py-1 font-mono font-black text-[var(--text)]">{r.ot_numero || '—'}</td>
                        <td className="px-2 py-1 text-[var(--text)]">{r.cliente_nombre}</td>
                        <td className="px-2 py-1 text-[var(--text-muted)]">{r.instalador_responsable_nombre || '—'}</td>
                        <td className="px-2 py-1 text-[var(--text-muted)]">{r.operador_nombre || '—'}</td>
                        <td className="px-2 py-1 text-[var(--text-muted)]">{r.fecha_inicio} → {r.fecha_fin}</td>
                        <td className="px-2 py-1 text-center">{r.cobrar_final ? <span className="text-red-600 font-black">SÍ</span> : ''}</td>
                      </tr>
                    ))}
                    {!data?.rows?.length && <tr><td colSpan={6} className="px-2 py-6 text-center text-[var(--text-muted)] italic">Sin programaciones en este rango</td></tr>}
                  </tbody>
                </table>
              ) : (
                <div className="space-y-3" data-testid="prev-detallado-list">
                  {(data?.rows || []).map((r, i) => (
                    <div key={r.id} className="border border-[var(--border)] rounded p-3 bg-[var(--surface-muted)]/50">
                      <p className="font-black text-blue-700 dark:text-blue-300 mb-1">#{i + 1} · {r.ot_numero || '(sin OT)'} · {r.cliente_nombre}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[var(--text)]">
                        <p><b className="text-[var(--text-muted)]">Dirección:</b> {r.direccion || '—'}</p>
                        <p><b className="text-[var(--text-muted)]">Vendedor:</b> {r.vendor_name || '—'}</p>
                        <p><b className="text-[var(--text-muted)]">Instalador:</b> {r.instalador_responsable_nombre || '—'} ({r.equipo_personas || 1} pers.)</p>
                        <p><b className="text-[var(--text-muted)]">Operador (admin):</b> {r.operador_nombre || '—'}</p>
                        <p><b className="text-[var(--text-muted)]">Vehículo:</b> {r.vehiculo || '—'} · <span className="font-mono">{r.placas || ''}</span></p>
                        <p><b className="text-[var(--text-muted)]">Inicio → Fin:</b> {r.fecha_inicio} → {r.fecha_fin}</p>
                        <p><b className="text-[var(--text-muted)]">Días est.:</b> {r.dias_estimados || '—'} · <b className="text-[var(--text-muted)]">HH extras:</b> {r.horas_extras || 0}</p>
                        <p><b className="text-[var(--text-muted)]">m²:</b> {r.m2 || '—'} · <b className="text-[var(--text-muted)]">mL:</b> {r.metros_lineales || '—'}</p>
                        <p><b className="text-[var(--text-muted)]">Flete/Viáticos:</b> Q {(r.flete_q || 0).toLocaleString()} / Q {(r.viaticos_q || 0).toLocaleString()}</p>
                      </div>
                      {r.descripcion_trabajo && <p className="text-xs mt-2"><b className="text-[var(--text-muted)]">Trabajo:</b> {r.descripcion_trabajo}</p>}
                      {r.observaciones && <p className="text-xs mt-1"><b className="text-[var(--text-muted)]">Obs:</b> {r.observaciones}</p>}
                      {r.cobrar_final && <p className="text-xs font-black text-red-600 mt-1">⚠ COBRAR PAGO FINAL al terminar</p>}
                    </div>
                  ))}
                  {!data?.rows?.length && <p className="text-center text-[var(--text-muted)] italic py-6">Sin programaciones en este rango</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sub-modal: elegir número WhatsApp */}
        {showWa && (
          <div className="absolute inset-0 z-10 bg-black/60 flex items-center justify-center p-4" onClick={() => !waSending && setShowWa(false)} data-testid="wa-picker-modal">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[12px] w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-black text-[var(--text)]">📱 Enviar PDF por WhatsApp</h3>
                <button onClick={() => !waSending && setShowWa(false)} disabled={waSending}><X size={18} /></button>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] mb-3">
                El PDF ({mode.toUpperCase()}) se enviará al número indicado usando la cuenta WhatsApp del negocio <b>{business?.name}</b>.
              </p>
              <p className="text-[9px] uppercase font-black text-[var(--text-muted)] mb-1">Contactos rápidos</p>
              <div className="flex gap-2 mb-3 flex-wrap">
                {quickContacts.map(c => (
                  <button key={c.phone} onClick={() => setWaPhone(c.phone)} disabled={waSending}
                          className={`px-3 py-1.5 text-xs font-bold rounded-full border ${waPhone === c.phone ? 'bg-green-600 border-green-700 text-white' : 'bg-[var(--surface-muted)] border-[var(--border)] text-[var(--text)] hover:bg-green-600/10'}`}
                          data-testid={`wa-quick-${c.name.toLowerCase()}`}>
                    {c.name} · +{c.phone}
                  </button>
                ))}
              </div>
              <label className="text-[9px] uppercase font-black text-[var(--text-muted)] block mb-1">O escribe el número (con o sin 502)</label>
              <input
                type="tel"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                placeholder="502xxxxxxxx o 5555-1234"
                disabled={waSending}
                className="w-full bg-[var(--surface-muted)] border border-[var(--border)] rounded px-3 py-2 text-sm text-[var(--text)] mb-4"
                data-testid="wa-phone-input"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowWa(false)} disabled={waSending} className="px-3 py-1.5 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text)]">Cancelar</button>
                <button onClick={sendWhatsapp} disabled={waSending || !waPhone.trim()}
                        className="px-4 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-xs font-bold"
                        data-testid="wa-send-btn">
                  {waSending ? 'Enviando…' : '📱 Enviar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

